import { Tree } from '@/dashboard/shared/utils/tree.utils';
import {
  ReferenceInfo,
  ReferenceVariable,
} from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/common/referenceSettingOption/referenceSettingOption.setup';
import { WidgetUUID } from '@/common/utils';
import { DisplayStyleType, WidgetChartType } from '@/dashboard/shared/types/widgets.types';
import { ArgParam } from '@/dashboard/features/widgetsAPI/domain/referenceParam';

/**
 * 차트 타입이 reference에 유효한지 확인
 * @param chartType - 차트 타입
 * @param displayStyle - 디스플레이 스타일 (옵션)
 */
export const isReferenceValidChartType = (
  chartType: WidgetChartType,
  displayStyle?: DisplayStyleType,
) => {
  switch (chartType) {
    case 'ACTION_VIEW':
    case 'STATUS_SUMMARY':
    case 'TOPOLOGY':
    case 'HEATMAP':
      return false;
    case 'STACKED_PROPORTION_BAR':
      return displayStyle !== 'MULTI_STACK';
    default:
      return true;
  }
};

/**
 * 위젯 reference 관계 트리 생성
 * @param widgetInfoList - 위젯 정보 리스트
 */
export const createWidgetReferenceRelation = (
  widgetInfoList: {
    widgetId: WidgetUUID;
    referenceInfo?: ReferenceInfo;
  }[],
) => {
  const relation = new Tree(null, 'root');

  widgetInfoList.forEach(({ widgetId: childLabel, referenceInfo }) => {
    if (referenceInfo) {
      Object.values(referenceInfo).forEach((variables: ReferenceVariable[] | undefined) => {
        variables?.forEach((variable) => {
          if (variable.value?.type === 'customVariable') {
            variable.value.selected.forEach(({ widgetId }) => {
              const parentLabel = widgetId;
              let parentNode = relation.findChild(parentLabel);
              if (!parentNode) {
                parentNode = relation.addChild(parentLabel);
              }
              const childNode = relation.findChild(childLabel);
              if (!childNode || !Tree.isDescendant(parentNode, childNode)) {
                parentNode.addChild(childLabel);
              }
            });
          }
        });
      });
    }
    if (!relation.findChild(childLabel)) {
      relation.addChild(childLabel);
    }
  });

  return relation;
};

/**
 * 커스텀 stat의 키 찾기
 * @param stat - ArgParam 객체
 */
export const findKeyCustomStat = (stat: ArgParam): string | null => {
  const excludedKeys = ['fromTime', 'toTime'];

  const foundEntry = Object.entries(stat).find(
    ([key, value]) => value.tags === true && !excludedKeys.includes(key),
  );

  return foundEntry ? foundEntry[0] : null;
};

/**
 * 차트 타입이 커스텀 stat 사용 불가능한지 확인
 * @param chartType - 차트 타입
 */
export const disabledUseCustomStat = (chartType: WidgetChartType) => {
  return ['ONE_DAY_COMPARE', 'ACTION_VIEW', 'GAUGE', 'HEATMAP'].includes(chartType);
};

/**
 * QueryManager stat ID인지 확인
 * @param statId - stat ID
 */
export const isQueryManagerStatId = (statId: string) => {
  return statId.startsWith('qm_');
};

/**
 * referenceInfo에 조건에 맞는 변수가 있는지 확인
 * @param referenceInfo - ReferenceInfo 객체
 * @param predicate - 변수 조건 함수
 * @returns 조건에 맞는 변수가 있는지 여부
 */
export const someReferenceVariable = (
  referenceInfo: ReferenceInfo,
  predicate: (variable: ReferenceVariable) => boolean,
): boolean => {
  return Object.values(referenceInfo).some((variableList) => variableList?.some(predicate));
};
